import { Box, DialogTitle, Divider, Grid, Typography } from "@mui/material";
import { EditEntry } from "../../../entriesAPI";
import { useTranslation } from "react-i18next";
import { AbsenceListEntry } from "../AbsenceHoursList/AbsenceHoursList";
import { useAppDispatch } from "../../../../../app/hooks";
import OverlaySpinner from "../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import PayrollList from "../../EditEntry/PayrollList/PayrollList";
import { useEffect, useState } from "react";
import { fetchEntryById } from "../../../entriesSlice";
import BasicInfo from "./BasicInfo";
import ArrowLeft from "../../../../../common/icons/arrowLeft.svg";
import EntrySupplements from "../../EditEntry/EntrySupplements/EntrySupplements";
import EntryCollapse from "../../Shared/EntryCollapse";
import EntryChanges from "../../EntryChanges";
import EntryLinks from "../../EntryLinks";

interface Props {
  entryToEdit: AbsenceListEntry;
  setEntryToEdit: (entry: AbsenceListEntry | null) => void;
  entries: AbsenceListEntry[];
  setEntries: (entries: AbsenceListEntry[]) => void;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
}

const AbsenceEdit = ({
  entryToEdit,
  setEntryToEdit,
  entries,
  setEntries,
  inputErrors,
  setInputErrors,
}: Props) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [entry, setEntry] = useState<EditEntry | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (entryToEdit?.id) {
      setIsLoading(true);
      dispatch(fetchEntryById(entryToEdit.id)).then((action: any) => {
        const responseData = action.payload;
        const entryData = responseData as EditEntry;

        setEntry({
          ...entryData,
          // Take start and end dates and lunch break from possibly already changed entry values in listing view
          start: entryToEdit.start,
          end: entryToEdit.end,
          lunchBreak: entryToEdit.lunchBreak,
        });
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, entryToEdit?.id]);

  // Every time entry in single row editing is changed, update entries list
  useEffect(() => {
    if (entry?.start && entry?.end) {
      const updatedEntries = entries.map((item) => {
        if (item.id === entry.id) {
          return {
            ...item,
            start: entry.start,
            end: entry.end,
            lunchBreak: entry.lunchBreak,
          };
        }
        return item;
      });
      setEntries([...updatedEntries]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  const goBack = () => {
    // Remove the input error from the array if it exists
    let errors = inputErrors;
    delete errors["absenceView_shift_start"];
    delete errors["absenceView_shift_end"];
    setInputErrors && setInputErrors({ ...errors });

    setEntryToEdit(null);
  };

  return (
    entry && (
      <Box sx={{ marginLeft: "16px", width: "100%" }}>
        <OverlaySpinner isLoading={isLoading} />
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              padding: "24px 32px",
              width: "100%",
            }}
          >
            <Box display="flex" alignItems="center" gap="32px">
              <DialogTitle sx={{ margin: "0px", padding: "0px" }}>
                <Typography
                  variant="header"
                  sx={{
                    fontWeight: 600,
                    minHeight: "32px",
                  }}
                >
                  <img
                    src={ArrowLeft}
                    alt={"< Back: "}
                    style={{ marginRight: "16px", cursor: "pointer" }}
                    onClick={() => goBack()}
                  />
                  {t("hourEntries:absenceTimeWorkHours").toUpperCase()}
                </Typography>
              </DialogTitle>
              <EntryLinks entry={entry} />
            </Box>
            <Grid container sx={{ display: "flex" }}>
              <BasicInfo
                entry={entry}
                setEntry={setEntry}
                inputErrors={inputErrors}
                setInputErrors={setInputErrors}
              />
              <EntrySupplements
                entry={entry}
                inputErrors={inputErrors}
                setInputErrors={setInputErrors}
                hideTitle={true}
                isDisabled={true}
                hideUnchecked={true}
              />
            </Grid>
            <Divider sx={{ marginTop: "24px", marginBottom: "24px" }} />
            <PayrollList
              entry={entry}
              setEntry={setEntry}
              editingActive={true}
            />
            <EntryCollapse
              openText={t("hourEntries:openChangelog")}
              closeText={t("hourEntries:closeChangelog")}
            >
              <EntryChanges entry={entry} />
            </EntryCollapse>
          </Box>
        </Box>
      </Box>
    )
  );
};

export default AbsenceEdit;
